"use client";

import React from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import Image from "next/image";
import { IconType } from "react-icons";

// Feature type definition
export interface Feature {
  title: string;
  description: string;
  icon: IconType;
  image: string;
  showBadge?: boolean;
}

// Mobile Stack Card Props
interface MobileStackCardProps {
  feature: Feature;
  index: number;
  totalCards: number;
  scrollYProgress: ReturnType<typeof useSpring>;
}

// Mobile Stack Card Component
export function MobileStackCard({
  feature,
  index,
  totalCards,
  scrollYProgress,
}: MobileStackCardProps) {
  const isFirstCard = index === 0;

  const segmentSize = 1 / totalCards;
  const startRange = index * segmentSize;
  const endRange = startRange + segmentSize;

  // Card slides up from bottom
  const y = useTransform(
    scrollYProgress,
    isFirstCard
      ? [0, 1]
      : [
          startRange,
          startRange + segmentSize * 0.3,
          startRange + segmentSize * 0.7,
          endRange,
        ],
    isFirstCard ? [0, 0] : [100, 40, 5, 0],
    { clamp: true }
  );

  const yVh = useTransform(y, (value) => `${value}vh`);

  const scale = useTransform(
    scrollYProgress,
    isFirstCard
      ? [0, 1]
      : [
          startRange,
          startRange + segmentSize * 0.4,
          startRange + segmentSize * 0.7,
        ],
    isFirstCard ? [1, 1] : [0.92, 0.97, 1]
  );

  // Apply rotation based on index
  const getRotation = (idx: number) => {
    if (idx === 0) return -5;
    if (idx === 1) return 5;
    if (idx === 2) return -5;
    return 0;
  };

  const rotation = getRotation(index);

  // Get background style based on card index
  const getMobileBackgroundStyle = (idx: number) =>
    idx === 1 ? "bg-white/10 backdrop-blur-md" : "bg-brand-black";

  return (
    <motion.div
      style={{
        y: yVh,
        scale,
        rotate: rotation,
        willChange: "transform",
      }}
      className={`absolute inset-0 flex w-full items-center justify-center ${index === 2 ? "z-20" : index === 1 ? "z-10" : "z-0"}`}
    >
      <div
        className={`${getMobileBackgroundStyle(index)} relative flex h-[400px] w-full flex-col rounded-[20px] border border-[#d92319] p-10 text-white ${index === 1 ? "custom-shadow" : ""}`}
      >
        {/* Top-left icon */}
        <div className="absolute top-4 left-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#544D4D69]">
          {(() => {
            const Icon: IconType = feature.icon as IconType;
            return <Icon className="text-2xl text-white" />;
          })()}
        </div>

        {/* Center image */}
        <div className="flex flex-1 items-center justify-center">
          <div className="relative h-32 w-full max-w-[240px]">
            <Image
              src={feature.image}
              alt={feature.title}
              fill
              className="object-contain opacity-80 grayscale"
              unoptimized
            />
          </div>
        </div>

        {/* Text content at bottom */}
        <div>
          <h3 className="font-raleway mb-2.5 text-xl font-bold">
            {feature.title}
          </h3>
          <p className="font-raleway text-base leading-[1.3] font-normal text-gray-300">
            {feature.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// Desktop Feature Card Props
interface FeatureCardProps {
  feature: Feature;
  index: number;
}

// Desktop Feature Card Component
export function FeatureCard({ feature, index }: FeatureCardProps) {
  // Desktop Rotation Logic
  const getDesktopStyles = (idx: number) => {
    if (idx === 0) return "md:rotate-[-5deg] md:z-0 md:mr-[-20px]";
    if (idx === 1) return "md:z-10 md:rotate-[5deg] md:scale-105 custom-shadow";
    if (idx === 2) return "md:rotate-[-5deg] md:z-0 md:ml-[-20px]";
    return "";
  };

  const getBackgroundStyle = (idx: number) =>
    idx === 1 ? "bg-white/10 backdrop-blur-md" : "bg-brand-black";

  const getTextColor = (idx: number) =>
    idx === 1 ? "text-black" : "text-white";

  return (
    <div
      className={`relative flex h-[400px] w-full flex-col rounded-[20px] border border-[#d92319] ${getBackgroundStyle(index)} p-10 ${getTextColor(index)} transition-all duration-300 hover:z-20 md:w-[350px] ${getDesktopStyles(index)}`}
    >
      {/* Top-left icon */}
      <div className="absolute top-4 left-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#544D4D69]">
        {(() => {
          const Icon: IconType = feature.icon as IconType;
          return <Icon className="text-2xl text-white" />;
        })()}
      </div>

      {/* Center image */}
      <div className="flex flex-1 items-center justify-center">
        <div className="relative h-32 w-full max-w-[240px]">
          <Image
            src={feature.image}
            alt={feature.title}
            fill
            className="object-contain opacity-80 grayscale"
            unoptimized
          />
        </div>
      </div>

      {/* Text content at bottom */}
      <div>
        <h3 className="font-raleway mb-2.5 text-xl font-bold text-white">
          {feature.title}
        </h3>
        <p className="font-raleway text-base leading-[1.3] font-normal text-gray-300">
          {feature.description}
        </p>
      </div>
    </div>
  );
}
